import * as React from "react";
import {Business, CurrentUser, LocationOffer, UserCard, UserType} from "../../types/Types";
import {useState} from "react";
import {useNavigate, useParams} from "react-router";
import {useEffect} from "react";
import {collection, limit, onSnapshot, query, where} from "@firebase/firestore";
import {db, functions} from "../../firebase/FirebaseProvider";
import PointsDisplay from "../../components/points/PointsDisplay";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBan, faCircleCheck, faCog, faStamp} from "@fortawesome/free-solid-svg-icons";
import {httpsCallable} from "@firebase/functions";
import OfferClaimList from "../../components/offer/OfferClaimList";
import {usageLimitBreached} from "../../utils/Utils";
import InfoAlert from "../../components/info/InfoAlert";
import OfferTicket from "../../components/offer/location/OfferTicket";
import OfferReceipt from "../../components/offer/location/OfferReceipt";

interface Props {
  currentUser: CurrentUser,
  businessMap: Map<string, Business>,
}

function OfferPage(props: Props) {
  const navigate = useNavigate();
  const [offer, setOffer] = useState<LocationOffer>();
  const [userCard, setUserCard] = useState<UserCard>();
  const [offerCard, setOfferCard] = useState<UserCard>();
  const [business, setBusiness] = useState<Business>();
  const [applying, setApplying] = useState<number>(0);

  const { id: urlParam } = useParams();
  const offerId = urlParam!.split("-")[0];
  const offerCardId = urlParam!.split("-")[1];

  useEffect(() => {
    onSnapshot(query(
        collection(db, 'offers'),
        where("id", "==", offerId),
        limit(1),
    ), (snapshot) => {
      snapshot.docChanges().forEach(offer => {
        if (offer.type === 'added' || offer.type === 'modified') {
          const offerToSet = offer.doc.data() as LocationOffer;
          setOffer(offerToSet);
          setBusiness(props.businessMap.get(offerToSet.business));
        }
      });
    });
  }, []);

  useEffect(() => {
    if(!offerCardId) {
      return
    }
    onSnapshot(query(
        collection(db, 'cards'),
        where("id", "==", offerCardId),
        limit(1),
    ), (snapshot) => {
      snapshot.docChanges().forEach(card => {
        if (card.type === 'added' || card.type === 'modified') {
          const cardToSet = card.doc.data() as UserCard;
          setOfferCard(cardToSet);
        }
      });
    });
  }, [offerCardId]);

  useEffect(() => {
    if(!business) {
      return;
    }
    if(props.currentUser.userType !== UserType.CUSTOMER) {
      return;
    }
    onSnapshot(query(
        collection(db, 'cards'),
        where("user", "==", props.currentUser.id),
        where("business", "==", business.id),
        where("status", "==", "ACTIVE"),
        limit(1),
    ), (snapshot) => {
      snapshot.docChanges().forEach(card => {
        if (card.type === 'added' || card.type === 'modified') {
          const cardToSet = card.doc.data() as UserCard;
          setUserCard(cardToSet);
        }
      });
    });
  }, [business]);

  const applyOffer = async () => {
    if(!offer || !business || !offerCard) {
      return;
    }

    setApplying(1);
    const claimOffer = httpsCallable(functions, 'claimOffer');
    await claimOffer({
      business: business.id,
      offer: offer.id,
      card: offerCard.id,
    }).then(() => {
      setApplying(2);
      setTimeout(() => { setApplying(3) }, 5000);
    });
  };

  if(business && offer && business.id !== offer.business) {
    navigate("/");
  }

  const getOfferButton = () => {
    if(props.currentUser.userType === UserType.CUSTOMER) {
      return <></>
    } else if(applying === 0 && offerCard && offer && usageLimitBreached(offerCard, offer)) {
      return <div className={"primary-button mt-2"}>Maximum Claims <FontAwesomeIcon className={"ms-1"} icon={faBan}/></div>
    } else if(applying === 0 && offerCard && offer && offerCard.points < offer.pointsCost) {
      return <div className={"primary-button mt-2"}>Insufficient Points <FontAwesomeIcon className={"ms-1"} icon={faBan}/></div>
    } else if(applying === 0 && business && offerCard && offer && offerCard.points >= offer.pointsCost) {
      return (<div className={"primary-button mt-2"} onClick={() => applyOffer()}>
        <div className={"d-inline-block me-2"}>Approve Claim <FontAwesomeIcon className={"ms-1"} icon={faStamp}/></div>
      </div>)
    } else if (applying === 1) {
      return <div className={"primary-button mt-2"}>Approving...</div>
    } else if (applying > 1) {
      return <div className={"primary-button mt-2"}>Approved <FontAwesomeIcon className={"ms-1"} icon={faCircleCheck}/></div>
    } else {
      return <></>
    }
  };

  const pointsCard = () => {
    return props.currentUser.userType === UserType.LOCATION_MANAGER ? offerCard : userCard
  };

  const canAffordOffer = () => {
    return pointsCard()!.points >= offer!.pointsCost
  };

  return (
      <>
        {offer && <div className={"centre-align"}>

          {(!offerCard && props.currentUser.userType === UserType.LOCATION_MANAGER) && <div className={"primary-button mb-3"} onClick={() => {}}>
              <div className={"d-inline-block me-2"}>Configure Offer</div>
              <FontAwesomeIcon icon={faCog}/>
          </div>}

          {(business && offerCard && props.currentUser.userType === UserType.LOCATION_MANAGER) && <div className={"basic-box mt-3 left-align"}>
              <span><b>{offerCard.userName}</b></span>
              <div className={"float-right font-06"}>
                  <PointsDisplay points={offerCard.points} />
              </div>
          </div>}

          {business && userCard && !canAffordOffer() && <div className={"mt-3"}>
              <InfoAlert message={"You have insufficient points."}/>
          </div>}

          <div className={"d-inline-block"}>
            <OfferTicket offer={offer} business={business} pointsCard={pointsCard()} showReceipt={props.currentUser.userType !== UserType.LOCATION_MANAGER}/>

            {(business && userCard && canAffordOffer() && !usageLimitBreached(userCard, offer) && props.currentUser.userType === UserType.CUSTOMER) && <div className={"centre-justify mb-2"}>
                <OfferReceipt offer={offer} business={business} pointsCard={pointsCard()!}/>
              </div>
            }
          </div>

          <div className={"mt-3"}>{getOfferButton()}</div>

          <OfferClaimList
              currentUser={props.currentUser}
              offerId={offerId}
              offerCard={props.currentUser.userType === UserType.LOCATION_MANAGER ? offerCard : userCard}
              defaultDisplayList={props.currentUser.userType === UserType.LOCATION_MANAGER}
              highlightNewClaim={applying === 2}
          />

        </div>}
      </>
  )
}

export default OfferPage;